import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { buildQuery, useApi } from './client'

export type PlannedTransaction = {
  id: string
  organizationId: string
  type: 'income' | 'expense' | 'transfer'
  plannedDate: string
  accountId: string
  toAccountId: string | null
  counterpartyId: string | null
  categoryId: string
  amount: number
  currency: 'UAH' | 'USD' | 'EUR'
  description: string | null
  recurringTemplateId: string | null
  status: 'pending' | 'completed' | 'cancelled'
  completedTransactionId: string | null
  createdAt: string
  updatedAt: string
}

export type PlannedFilter = {
  organizationId?: string
  from?: string
  to?: string
  type?: 'income' | 'expense' | 'transfer'
  status?: 'pending' | 'completed' | 'cancelled'
}

export type PlannedCreateInput = Omit<
  PlannedTransaction,
  | 'id'
  | 'status'
  | 'completedTransactionId'
  | 'createdAt'
  | 'updatedAt'
  | 'toAccountId'
  | 'counterpartyId'
  | 'description'
  | 'recurringTemplateId'
> & {
  toAccountId?: string
  counterpartyId?: string
  description?: string
  recurringTemplateId?: string
}

export type PlannedPatchInput = Partial<
  Omit<PlannedTransaction, 'id' | 'organizationId' | 'createdAt' | 'updatedAt' | 'completedTransactionId'>
>

export type CompleteInput = {
  actualDate?: string
  actualAmount?: number
  transactionId?: string
}

const ROOT = '/planned-transactions'
const KEY = ['planned'] as const

type ListResp = { items: PlannedTransaction[] }

export function usePlanned(filter: PlannedFilter = {}) {
  const api = useApi()
  return useQuery({
    queryKey: [...KEY, 'list', filter],
    queryFn: () =>
      api<ListResp>(
        `${ROOT}${buildQuery({
          organization_id: filter.organizationId,
          from: filter.from,
          to: filter.to,
          type: filter.type,
          status: filter.status,
        })}`,
      ),
  })
}

export function useCreatePlanned() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: PlannedCreateInput) =>
      api<PlannedTransaction>(ROOT, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUpdatePlanned() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: string } & PlannedPatchInput) =>
      api<PlannedTransaction>(`${ROOT}/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useDeletePlanned() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api<void>(`${ROOT}/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useCompletePlanned() {
  const api = useApi()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & CompleteInput) =>
      api<{ transactionId: string }>(`${ROOT}/${id}/complete`, { method: 'POST', body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY })
      qc.invalidateQueries({ queryKey: ['transactions'] })
    },
  })
}
